import {
  OfflineRechargeStatus,
  Order,
  OrderLog,
  PedidoPosOrderLog,
  PrintStatus,
  ProdutoPedidoPosOrderLog,
  RePrintOrderSyncObject,
} from "../../../domains/order/agreggates/order/Order";
import { IOrderRepositoryDB } from "../../../domains/order/interfaces/repositories/IOrderRepositoryDB";
import { formatISO } from "date-fns";
import localforage from "localforage";
const KEY = "@meep/Orders";

export interface IIndexedOrderRepository {
  add(order: OrderLog, log: string): Promise<void>;
  findById(id: string): Promise<Order | null>;
  all(): Promise<Array<Order>>;
}

export const OrderRepositoryLocalStorage = (): IOrderRepositoryDB => {
  const add = async (
    order: OrderLog,
    log: string,
    pix?: string,
    printStatus?: PrintStatus
  ): Promise<void> => {
    try {
      let orders = (await localforage.getItem<OrderLog[]>(KEY)) ?? [];
      const index = orders.findIndex((it) => it?.id === order.id);
      if (index >= 0) {
        let pixLogs = [...(orders[index]?.pixLog ?? [])];
        let logs = [...(orders[index]?.log ?? [])];
        if (pix) {
          pixLogs.push({
            time: formatISO(new Date()),
            message: log,
            pix: pix,
          });
        } else {
          logs.push({
            time: formatISO(new Date()),
            message: log,
          });
        }
        let orderItems = order?.orderItems ?? orders[index]?.orderItems;

        if (order?.pedidoPOS) {
          orderItems.forEach((it) => {
            order.pedidoPOS!.produtosObject.forEach((pos) => {
              if (pos.produtoId === it.productId) {
                it.ticketIds = it.ticketIds || [];
                if (it.ticketIds.findIndex((t) => t === pos.ticketId) === -1) {
                  const exists = orderItems.some((o) =>
                    o.ticketIds?.includes(pos.ticketId)
                  );
                  if (!exists) {
                    it.ticketIds.push(pos.ticketId);
                  }
                }
              }
            });
          });
        }

        orders[index] = {
          ...orders[index],
          isDataRecorded:
            "isDataRecorded" in order
              ? order.isDataRecorded
              : "isDataRecorded" in orders[index]
              ? orders[index].isDataRecorded
              : null,
          isOfflineRecharge:
            order?.isOfflineRecharge ?? orders[index]?.isOfflineRecharge,
          offlineRechargeStatus:
            order?.offlineRechargeStatus ?? orders[index].offlineRechargeStatus,
          payment: order?.payment ?? orders[index]?.payment,
          paymentPix: order?.paymentPix ?? orders[index]?.paymentPix,
          pedidoPOS: order?.pedidoPOS
            ? order.pedidoPOS
            : orders[index]?.pedidoPOS,
          paymentApproved:
            order?.paymentApproved ?? orders[index]?.paymentApproved,
          nfce: order?.nfce ?? orders[index]?.nfce,
          orderItems,
          totalValue: order?.totalValue ?? orders[index]?.totalValue,
          log: logs,
          pixLog: pixLogs,
          printStatus: printStatus ? printStatus : orders[index]?.printStatus,
          printType: order?.printType ?? orders[index]?.printType,
          rechargeType: order?.rechargeType ?? orders[index]?.rechargeType,
        };
      } else {
        const orderToAddLog = {
          ...order,
          log: [
            {
              time: formatISO(new Date()),
              message: log,
            },
          ],
        };
        orders.push(orderToAddLog);
      }
      if (orders.length > 200) {
        await localforage.setItem(
          KEY,
          orders.filter((_, index) => index > 100)
        );
      } else {
        await localforage.setItem(
          KEY,
          orders.filter((it) => it)
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getAllPendingRecharge = async (): Promise<OrderLog[]> => {
    try {
      const orders = (await localforage.getItem<OrderLog[]>(KEY)) ?? [];
      return (
        orders.filter(
          (order) =>
            order.offlineRechargeStatus === OfflineRechargeStatus.RECHARGE
        ) ?? []
      );
    } catch (error) {
      return [];
    }
  };

  const findPendingRecharge = async (
    associationId: string
  ): Promise<OrderLog | null> => {
    try {
      const orders = (await localforage.getItem<OrderLog[]>(KEY)) ?? [];
      return (
        orders.find(
          (order) =>
            order.customer?.associationId === associationId &&
            order.offlineRechargeStatus === OfflineRechargeStatus.RECHARGE
        ) ?? null
      );
    } catch (error) {
      return null;
    }
  };

  const findById = async (id: string): Promise<OrderLog | null> => {
    try {
      const orders = (await localforage.getItem<OrderLog[]>(KEY)) ?? [];
      return orders.find((order) => order.id === id) ?? null;
    } catch (error) {
      return null;
    }
  };

  const all = async (): Promise<OrderLog[]> => {
    try {
      const orders = (await localforage.getItem<OrderLog[]>(KEY)) ?? [];
      return orders.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error) {
      return [];
    }
  };

  const updateRePrintTicket = async (
    ticketId: string,
    rePrintRequest: RePrintOrderSyncObject
  ) => {
    const orders: OrderLog[] =
      (await localforage.getItem<OrderLog[]>(KEY)) ?? [];

    const newOrders = orders.map((orderLog: OrderLog) => {
      if (orderLog?.pedidoPOS) {
        const newProdutosObject = orderLog.pedidoPOS?.produtosObject?.map(
          (produtoPOSOrderLog) => {
            if (produtoPOSOrderLog.ticketId === ticketId) {
              const newProdutoPos: ProdutoPedidoPosOrderLog = {
                ...produtoPOSOrderLog,
                reprinted: produtoPOSOrderLog.reprinted
                  ? [...produtoPOSOrderLog.reprinted, rePrintRequest]
                  : [rePrintRequest],
              };
              return newProdutoPos;
            }
            return produtoPOSOrderLog;
          }
        );

        const newPedidoPos: PedidoPosOrderLog = {
          ...orderLog.pedidoPOS,
          produtosObject: newProdutosObject,
        };
        return { ...orderLog, pedidoPOS: newPedidoPos };
      } else return orderLog;
    });

    await localforage.setItem(KEY, newOrders);
  };

  const updateRePrintTicketList = async (
    ticketIds: string[],
    rePrintRequest: RePrintOrderSyncObject
  ) => {
    const orders: OrderLog[] =
      (await localforage.getItem<OrderLog[]>(KEY)) ?? [];
    const ids = new Set(ticketIds); // lookup O(1)

    const newOrders = orders.map((orderLog) => {
      if (!orderLog?.pedidoPOS) return orderLog;

      const newProdutosObject = orderLog.pedidoPOS.produtosObject?.map(
        (produto) => {
          if (produto && ids.has(produto.ticketId)) {
            return {
              ...produto,
              reprinted: produto.reprinted
                ? [...produto.reprinted, rePrintRequest]
                : [rePrintRequest],
            } as ProdutoPedidoPosOrderLog;
          }
          return produto;
        }
      );

      return {
        ...orderLog,
        pedidoPOS: {
          ...orderLog.pedidoPOS,
          produtosObject: newProdutosObject,
        },
      } as OrderLog;
    });

    await localforage.setItem(KEY, newOrders);
  };

  const clearOldSessionOrders = async (sessionId: string) => {
    const orders: OrderLog[] =
      (await localforage.getItem<OrderLog[]>(KEY)) ?? [];

    const currentSessionOrders = orders.filter(
      (it) => it.sessionId === sessionId
    );

    await localforage.setItem(KEY, currentSessionOrders);
  };

  return {
    add,
    findById,
    all,
    updateRePrintTicket,
    findPendingRecharge,
    getAllPendingRecharge,
    clearOldSessionOrders,
    updateRePrintTicketList
  };
};
